import React, { useState } from 'react';
import { JsonView } from 'react-json-view-lite';
import { SafeMarkdown } from './ui/SafeMarkdown.js';
import { Copy, Check, Code, Eye } from 'lucide-react';
import { Button } from './ui/Button.js';
import { parseContentSegments } from '../utils/contentParser.js';
import { StepOutputViewer, isAgentStepOutput } from './agents/output/index.js';

/** Custom theme — uses jv-* structural classes from index.css + Tailwind colors */
const jsonTheme = {
  container: 'jv-container text-sm font-mono',
  basicChildStyle: 'jv-child',
  label: 'jv-label text-accent-text',
  clickableLabel: 'jv-label jv-clickable text-accent-text',
  nullValue: 'text-faint italic',
  undefinedValue: 'text-faint italic',
  stringValue: 'text-body',
  booleanValue: 'text-warning',
  numberValue: 'text-accent-text',
  otherValue: 'text-body',
  punctuation: 'jv-punctuation text-faint',
  expandIcon: 'jv-expand text-muted cursor-pointer inline-block',
  collapseIcon: 'jv-collapse text-muted cursor-pointer inline-block',
  collapsedContent: 'jv-collapsed text-faint cursor-pointer',
  noQuotesForStringValues: false,
};

interface RichContentRendererProps {
  content: string;
  isStreaming?: boolean;
  className?: string;
}

/** JSON block with raw/tree toggle and copy button */
const JsonBlock: React.FC<{ data: unknown; raw: string }> = ({ data, raw }) => {
  const [rawMode, setRawMode] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = rawMode ? raw : JSON.stringify(data, null, 2);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="rounded-lg border border overflow-hidden">
      <div className="bg-surface-alt px-3 py-1.5 flex items-center justify-between border-b border">
        <span className="text-xs text-muted font-mono">JSON</span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={() => setRawMode(!rawMode)}
            aria-label={rawMode ? 'Tree view' : 'Raw view'}
          >
            {rawMode ? <Eye size={12} /> : <Code size={12} />}
          </Button>
          <Button variant="ghost" size="sm" iconOnly onClick={handleCopy} aria-label="Copy">
            {copied ? <Check size={12} className="text-success" /> : <Copy size={12} />}
          </Button>
        </div>
      </div>
      <div className="p-2 bg-bg overflow-auto max-h-[600px]">
        {rawMode ? (
          <pre className="text-sm font-mono text-body whitespace-pre-wrap leading-relaxed">
            {JSON.stringify(data, null, 2)}
          </pre>
        ) : (
          // JsonView's own type declares `data: Object | Array<any>` — the segment is `unknown`
          // (arbitrary parsed JSON), and this is the one place it crosses into that third-party
          // prop contract.
          <JsonView data={data as object} shouldExpandNode={(level) => level < 2} style={jsonTheme} />
        )}
      </div>
    </div>
  );
};

export const RichContentRenderer: React.FC<RichContentRendererProps> = ({
  content,
  isStreaming = false,
  className = '',
}) => {
  if (!content) {
    return <pre className={`whitespace-pre-wrap font-mono text-sm leading-relaxed ${className}`}>{content}</pre>;
  }

  // During streaming: render markdown but skip JSON block parsing (may be incomplete)
  if (isStreaming) {
    return (
      <div className={`prose prose-sm dark:prose-invert max-w-none ${className}`}>
        <SafeMarkdown
          forceBlock
          overrides={{
            h1: { props: { className: 'text-xl font-bold mb-3 mt-4 text-heading' } },
            h2: { props: { className: 'text-lg font-semibold mb-2 mt-3 text-heading' } },
            h3: { props: { className: 'text-base font-semibold mb-2 mt-2 text-heading' } },
            p: { props: { className: 'mb-2 leading-relaxed text-body text-sm' } },
            ul: { props: { className: 'list-disc pl-5 mb-2 space-y-1 text-sm text-body' } },
            ol: { props: { className: 'list-decimal pl-5 mb-2 space-y-1 text-sm text-body' } },
            li: { props: { className: 'text-sm' } },
            code: { props: { className: 'bg-surface-alt px-1.5 py-0.5 rounded-sm text-xs font-mono text-heading' } },
            pre: { props: { className: 'bg-surface-alt p-3 rounded-lg overflow-auto text-xs font-mono my-2' } },
            strong: { props: { className: 'font-semibold text-heading' } },
            em: { props: { className: 'italic' } },
            blockquote: { props: { className: 'border-l-4 border pl-4 italic text-muted my-2' } },
          }}
        >
          {content}
        </SafeMarkdown>
      </div>
    );
  }

  const segments = parseContentSegments(content);

  return (
    <div className={`space-y-3 ${className}`}>
      {segments.map((segment, i) => {
        if (segment.type === 'json' && segment.parsed) {
          if (isAgentStepOutput(segment.parsed)) {
            return <StepOutputViewer key={i} data={segment.parsed as Record<string, unknown>} />;
          }
          return <JsonBlock key={i} data={segment.parsed} raw={segment.content} />;
        }

        // Prose — render as markdown
        return (
          <div key={i} className="prose prose-sm dark:prose-invert max-w-none">
            <SafeMarkdown
              overrides={{
                h1: { props: { className: 'text-xl font-bold mb-3 mt-4 text-heading' } },
                h2: { props: { className: 'text-lg font-semibold mb-2 mt-3 text-heading' } },
                h3: { props: { className: 'text-base font-semibold mb-2 mt-2 text-heading' } },
                p: { props: { className: 'mb-2 leading-relaxed text-body text-sm' } },
                ul: { props: { className: 'list-disc pl-5 mb-2 space-y-1 text-sm text-body' } },
                ol: { props: { className: 'list-decimal pl-5 mb-2 space-y-1 text-sm text-body' } },
                li: { props: { className: 'text-sm' } },
                code: {
                  props: { className: 'bg-surface-alt px-1.5 py-0.5 rounded-sm text-xs font-mono text-heading' },
                },
                pre: { props: { className: 'bg-surface-alt p-3 rounded-lg overflow-auto text-xs font-mono my-2' } },
                strong: { props: { className: 'font-semibold text-heading' } },
                em: { props: { className: 'italic' } },
                blockquote: { props: { className: 'border-l-4 border pl-4 italic text-muted my-2' } },
                table: { props: { className: 'border-collapse w-full text-sm my-2' } },
                thead: { props: { className: '' } },
                tbody: { props: { className: '' } },
                tr: { props: { className: '' } },
                th: { props: { className: 'border border px-3 py-1.5 bg-bg text-left font-medium text-body' } },
                td: { props: { className: 'border border px-3 py-1.5 text-body' } },
              }}
            >
              {segment.content}
            </SafeMarkdown>
          </div>
        );
      })}
    </div>
  );
};
